import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * Entity types supported by the relationship system
 */
export type EntityType = 'contract' | 'vendor' | 'person' | 'baa' | 'coi' | 'legalEntity' | 'site';

/**
 * Represents an entity reference in the relationship system
 */
export interface EntityReference {
  type: EntityType;
  id: string;
}

/**
 * Request to add a relationship
 */
export interface AddRelationshipRequest {
  sourceEntity: EntityReference;
  field: string;
  targetValue: string | string[]; // Can be single ID or array of IDs
}

/**
 * Request to remove a relationship
 */
export interface RemoveRelationshipRequest {
  sourceEntity: EntityReference;
  field: string;
  targetValue: string | string[]; // Can be single ID or array of IDs
}

/**
 * RelationshipService - Manages bidirectional relationships between entities
 *
 * This service communicates with the backend relationship API to ensure
 * that when relationships are created/removed, both sides of the relationship
 * are updated automatically.
 *
 * Example: When a contract adds a vendor as directVendor:
 *  - Contract.directVendor = "VENDOR123"
 *  - Vendor.contracts += "CONTRACT456" (handled by backend)
 *  - All associated people are also linked (handled by backend)
 */
@Injectable({
  providedIn: 'root'
})
export class RelationshipService {
  private http = inject(HttpClient);

  private readonly headers = new HttpHeaders({
    'Content-Type': 'application/json'
  });

  /**
   * Adds a relationship between two entities
   *
   * @param request The relationship to add
   * @returns Promise that resolves when the relationship is created
   */
  async add(request: AddRelationshipRequest): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(environment.URL_RELATIONSHIPS_ADD, request, { headers: this.headers })
      );
    } catch (error) {
      console.error('RelationshipService | Error adding relationship:', error);
      throw error;
    }
  }

  /**
   * Removes a relationship between two entities
   *
   * @param request The relationship to remove
   * @returns Promise that resolves when the relationship is removed
   */
  async remove(request: RemoveRelationshipRequest): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(environment.URL_RELATIONSHIPS_REMOVE, request, { headers: this.headers })
      );
    } catch (error) {
      console.error('RelationshipService | Error removing relationship:', error);
      throw error;
    }
  }

  /**
   * Updates a relationship by removing the old value and adding the new value
   * Use this when a relationship field changes from one value to another
   *
   * @param sourceEntity The entity being updated
   * @param field The relationship field name
   * @param oldValue The old relationship value (to remove)
   * @param newValue The new relationship value (to add)
   */
  async update(
    sourceEntity: EntityReference,
    field: string,
    oldValue: string | string[] | null | undefined,
    newValue: string | string[] | null | undefined
  ): Promise<void> {
    // Remove old relationship if it exists
    if (oldValue) {
      await this.remove({
        sourceEntity,
        field,
        targetValue: oldValue
      });
    }

    // Add new relationship if it exists
    if (newValue) {
      await this.add({
        sourceEntity,
        field,
        targetValue: newValue
      });
    }
  }
}
